import api from "api";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { queryKeys } from "utils/misc";
import { supportApiBase } from "api/hooks/useHCPs";
import { ServiceItem, ServiceCategory, CreateServiceInput, CreateServiceCategoryInput } from "routes/hcp/types";

export const useHcpServices = (hcpId: number) => {
  const key = queryKeys.hcpServices(hcpId);
  return useQuery(key, async () => {
    const response = await api.call(`/health_care_providers/${hcpId}/services`, { root: supportApiBase });
    const data = await response.json();
    return (data?.services ?? []) as ServiceItem[];
  }, { enabled: hcpId > 0 });
};

export const useCreateHcpService = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async ({ hcpId, ...data }: CreateServiceInput & { hcpId: number }) => {
      const response = await api.call(`/health_care_providers/${hcpId}/services`, {
        method: "POST",
        data,
        root: supportApiBase,
      });
      return response.json() as Promise<ServiceItem>;
    },
    {
      onSuccess: (_, { hcpId }) => {
        queryClient.invalidateQueries(queryKeys.hcpServices(hcpId));
      },
    }
  );
};

export const useCreateHcpServiceCategory = () => {
  return useMutation(
    async ({ hcpId, ...data }: CreateServiceCategoryInput & { hcpId: number }) => {
      const response = await api.call(`/health_care_providers/${hcpId}/service_categories`, {
        method: "POST",
        data,
        root: supportApiBase,
      });
      return response.json() as Promise<ServiceCategory>;
    }
  );
};

export const useCreateDefaultBooking = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async (hcpId: number) => {
      await api.call(`/health_care_providers/${hcpId}/default_booking`, {
        method: "POST",
        root: supportApiBase,
      });
    },
    {
      onSuccess: (_, hcpId) => {
        queryClient.invalidateQueries(queryKeys.hcpServices(hcpId));
      },
    }
  );
};
